import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DollarSign, AlertCircle, Wallet } from "lucide-react";
import { formatCost } from "@/utils/costEstimation";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CostEstimator } from "@/components/CostEstimator";

interface CostConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  cost: number;
  title: string;
  description: string;
  currentBudget?: number;
  breakdown?: {
    imageCount?: number;
    audioCount?: number;
    videoCount?: number;
    documentCount?: number;
    contactCount?: number;
    summaryIncluded?: boolean;
  };
}

export function CostConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  cost,
  title,
  description,
  currentBudget,
  breakdown,
}: CostConfirmDialogProps) {
  const hasInsufficientBudget = currentBudget !== undefined && currentBudget < cost;
  const remainingBudget = currentBudget !== undefined ? currentBudget - cost : undefined;

  return (
    <TooltipProvider>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p>{description}</p>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3">
            {/* Cost Breakdown using CostEstimator */}
            {breakdown && (
              <CostEstimator
                imageCount={breakdown.imageCount || 0}
                audioCount={breakdown.audioCount || 0}
                videoCount={breakdown.videoCount || 0}
                documentCount={breakdown.documentCount || 0}
                contactCount={breakdown.contactCount || 0}
                includesSummary={breakdown.summaryIncluded}
                showTooltips={true}
              />
            )}

            {/* Simple cost display if no breakdown */}
            {!breakdown && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">עלות משוערת:</span>
                  <span className="text-2xl font-bold text-primary">
                    {formatCost(cost)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  * העלות בפועל עשויה להשתנות בהתאם להמאפיינים למרובים בקובץ המקור
                </p>
              </div>
            )}

            {/* Budget Information */}
            {currentBudget !== undefined && (
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    תקציב נוכחי
                  </span>
                  <span className="font-medium">{formatCost(currentBudget)}</span>
                </div>
                
                {!hasInsufficientBudget && remainingBudget !== undefined && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">לאחר הפעולה</span>
                    <span className="font-medium text-primary">{formatCost(remainingBudget)}</span>
                  </div>
                )}

                {/* Insufficient Budget Warning */}
                {hasInsufficientBudget && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      תקציב לא מספיק. נדרש {formatCost(cost - currentBudget)} נוסף
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>ביטול</AlertDialogCancel>
            <AlertDialogAction 
              onClick={onConfirm}
              disabled={hasInsufficientBudget}
            >
              {hasInsufficientBudget ? "תקציב לא מספיק" : "אשר והמשך"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
